import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import FooterSection from "@/pages/home/components/FooterSection";
import { seoArticleContents, featuredArticles } from "@/mocks/seo-content";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://pandabuy.surf";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = slug ? seoArticleContents[slug] : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    if (article) {
      document.title = `${article.title} | PandabuySpreadsheet Directory`;
    }
  }, [slug, article]);

  if (!article) {
    return (
      <main>
        <Navbar />
        <div className="pt-24 md:pt-28 pb-16 md:pb-24">
          <div className="px-6 md:px-10 max-w-3xl mx-auto text-center">
            <h1 className="font-display text-3xl font-bold text-foreground-900 mb-4">Article Not Found</h1>
            <p className="text-foreground-500 mb-6">The article you are looking for does not exist.</p>
            <Link
              to="/"
              className="inline-flex items-center px-6 py-2.5 text-sm font-semibold rounded-full bg-primary-500 text-white whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              Back to Homepage
            </Link>
          </div>
        </div>
        <FooterSection />
      </main>
    );
  }

  const { title, date, category, readTime, imageUrl, sections } = article;
  const articleUrl = `${siteUrl}/article/${slug}`;
  const articleDescription = sections[0]?.content?.substring(0, 160) || title;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": articleDescription,
    "image": imageUrl,
    "datePublished": date,
    "dateModified": date,
    "author": {
      "@type": "Organization",
      "name": "PandabuySpreadsheet Directory",
      "url": siteUrl,
    },
    "publisher": {
      "@type": "Organization",
      "name": "PandabuySpreadsheet Directory",
      "url": siteUrl,
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg",
      },
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": articleUrl,
    },
  };

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />

      <Navbar />

      <article className="pt-24 md:pt-28 pb-16 md:pb-24">
        <div className="px-6 md:px-10 max-w-3xl mx-auto">
          <div className="mb-10">
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back to PandabuySpreadsheet Directory
            </Link>
          </div>

          <header className="mb-12">
            <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
              {category}
            </span>
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mt-4 leading-tight">
              {title}
            </h1>
            <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500">
              <span>Updated: {date}</span>
              <span>&middot;</span>
              <span>{readTime}</span>
            </div>
          </header>

          <div className="rounded-2xl overflow-hidden mb-12">
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-auto"
            />
          </div>

          <div className="prose-custom">
            {sections.map((section, idx) => (
              <div key={idx} className="mb-10">
                <h2 className="font-display text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {section.heading}
                </h2>
                {section.content.split("\n\n").map((paragraph, pIdx) => (
                  <p key={pIdx} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
            ))}
          </div>

          <div className="mt-14 p-6 md:p-8 rounded-2xl bg-primary-50 border border-primary-100">
            <h3 className="font-display text-lg font-bold text-foreground-900 mb-2">
              Ready to Start Browsing?
            </h3>
            <p className="text-sm text-foreground-600 mb-4">
              Explore our curated PandabuySpreadsheet directory with 14 categories and 10,000+ verified finds — updated daily.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="inline-flex items-center justify-center px-6 py-2.5 text-sm font-semibold rounded-full bg-primary-500 text-white whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                Back to Homepage
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center px-6 py-2.5 text-sm font-semibold rounded-full border border-primary-300 text-primary-700 whitespace-nowrap hover:bg-primary-100 transition-colors"
              >
                pandabuy sheet <i className="ri-external-link-line ml-1"></i>
              </a>
            </div>
          </div>

          <div className="mt-10 p-6 rounded-xl bg-background-100 border border-background-200/70">
            <h4 className="text-sm font-semibold text-foreground-900 mb-3">More PandabuySpreadsheet Guides</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {featuredArticles
                .filter((a) => a.slug !== slug)
                .slice(0, 4)
                .map((a) => (
                  <Link
                    key={a.id}
                    to={`/article/${a.slug}`}
                    className="text-xs text-primary-600 hover:text-primary-700 transition-colors py-1"
                  >
                    {a.title}
                  </Link>
                ))}
            </div>
          </div>

          <div className="mt-10 pt-8 border-t border-background-200">
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back to PandabuySpreadsheet Directory
            </Link>
          </div>
        </div>
      </article>

      <FooterSection />
    </main>
  );
}